import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEO from '../components/SEO';
import Footer from '../components/Footer';

// Icons
const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const ScaleIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="M7 21h10" />
    <path d="M12 3v18" />
    <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
  </svg>
);

const FileTextIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

const GavelIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m14 13-7.5 7.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 0 1 0-3L11 10" />
    <path d="m16 16 6-6" />
    <path d="m8 8 6-6" />
    <path d="m9 7 8 8" />
    <path d="m21 11-8-8" />
  </svg>
);

const QuoteVsEstimateGuide = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Structured data for Guide/Article
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article", 
    "headline": "Quote vs Estimate: What is the Difference? (2025 Guide)",
    "description": "Learn the legal and practical differences between a price quote and an estimate. Find out which one you should send to win the job.",
    "author": {
      "@type": "Organization",
      "name": "QuoteTier"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://quotetier.com/quote-vs-estimate"
    }
  };

  useEffect(() => {
    analytics.track('QuoteVsEstimate Page View');
  }, []);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Quote vs Estimate: What's the Difference? | 2025 Guide"
        description="Quote vs Estimate: understand the key legal differences. Learn when to use a binding quote vs a rough estimate to protect your business."
        canonical="/quote-vs-estimate"
        structuredData={structuredData}
        ogTitle="Quote vs Estimate: The Definitive Guide for Small Business"
        ogDescription="One is a guess, the other is a contract. Learn the difference and see free templates for both."
      />
      {/* Header */}
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'navigation', action: 'signup' })}>
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/blog/" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'mobile-navigation', action: 'signup' })}>
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-24 md:py-32 bg-gray-50">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-sm font-medium text-gray-800 mb-6">
              <ScaleIcon className="mr-2 h-4 w-4" />
              Small Business Guide
            </div>
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl tracking-tight leading-tight mb-6">
              Quote vs. Estimate:<br />
              <span className="text-primary font-bold">What's the Difference?</span>
            </h1>
            <p className="font-jetbrains text-xl text-gray-600 tracking-tight leading-relaxed mb-8 max-w-2xl mx-auto">
              One is a "best guess" and the other is a fixed price. Sending the wrong one can cost you thousands. Here is the definitive guide on when to use each.
            </p>
            <div className="font-instrument mt-8 flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'guide-hero', action: 'signup' })}>
                <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Create a Quote Now
                </Button>
              </Link>
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'guide-hero', action: 'estimate' })}>
                <Button className="px-8 py-4 text-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-white rounded-lg" variant="outline">
                  Create an Estimate Now
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* The Core Difference */}
        <section className="py-16 md:py-24">
          <div className="container max-w-5xl mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-2">
              {/* Estimate Card */}
              <div className="bg-blue-50/50 rounded-3xl p-8 border border-blue-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                    <FileTextIcon className="h-5 w-5" />
                  </div>
                  <h2 className="font-instrument text-3xl font-bold">Estimate</h2>
                </div>
                <p className="font-jetbrains text-lg font-medium text-gray-900 mb-4">
                  "This is roughly what it will cost."
                </p>
                <p className="font-jetbrains text-gray-600 mb-6">
                  An estimate is an educated guess based on your experience. It is <strong>subject to change</strong> if the scope of work changes or if you discover unforeseen issues.
                </p>
                <ul className="space-y-3 font-jetbrains text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CheckIcon className="h-4 w-4 mt-1 text-blue-500" />Best for complex jobs with unknowns</li>
                  <li className="flex items-start gap-2"><CheckIcon className="h-4 w-4 mt-1 text-blue-500" />Allows for price flexibility</li>
                  <li className="flex items-start gap-2"><CheckIcon className="h-4 w-4 mt-1 text-blue-500" />Should include a variance (e.g., +/- 10%)</li>
                </ul>
              </div>

              {/* Quote Card */}
              <div className="bg-green-50/50 rounded-3xl p-8 border border-green-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                    <GavelIcon className="h-5 w-5" />
                  </div>
                  <h2 className="font-instrument text-3xl font-bold">Quote</h2>
                </div>
                <p className="font-jetbrains text-lg font-medium text-gray-900 mb-4">
                  "This is exactly what you will pay."
                </p>
                <p className="font-jetbrains text-gray-600 mb-6">
                  A quote is a <strong>fixed price offer</strong>. Once the client accepts it, you cannot change the price unless they request additional work (a change order).
                </p>
                <ul className="space-y-3 font-jetbrains text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CheckIcon className="h-4 w-4 mt-1 text-green-500" />Best for standard, repeatable jobs</li>
                  <li className="flex items-start gap-2"><CheckIcon className="h-4 w-4 mt-1 text-green-500" />Legally binding once accepted</li>
                  <li className="flex items-start gap-2"><CheckIcon className="h-4 w-4 mt-1 text-green-500" />Builds higher trust with clients</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 bg-white border-y border-gray-100">
          <div className="container max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl font-bold mb-4">At A Glance Comparison</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-100">
                    <th className="py-4 px-4 font-instrument text-lg text-gray-500 font-medium">Factor</th>
                    <th className="py-4 px-4 font-instrument text-lg font-bold text-blue-600">Estimate</th>
                    <th className="py-4 px-4 font-instrument text-lg font-bold text-green-600">Quote</th>
                  </tr>
                </thead>
                <tbody className="font-jetbrains text-sm text-gray-700">
                  <tr className="border-b border-gray-50">
                    <td className="py-4 px-4 font-medium">Price Certainty</td>
                    <td className="py-4 px-4">Flexible (Approximation)</td>
                    <td className="py-4 px-4">Fixed (Exact Price)</td>
                  </tr>
                  <tr className="border-b border-gray-50">
                    <td className="py-4 px-4 font-medium">Legal Status</td>
                    <td className="py-4 px-4">Non-Binding (Usually)</td>
                    <td className="py-4 px-4">Binding Contract</td>
                  </tr>
                  <tr className="border-b border-gray-50">
                    <td className="py-4 px-4 font-medium">When to Use</td>
                    <td className="py-4 px-4">Unknown scope (Renovations, Repairs)</td>
                    <td className="py-4 px-4">Known scope (Installations, Packages)</td>
                  </tr>
                  <tr className="border-b border-gray-50">
                    <td className="py-4 px-4 font-medium">Client Preference</td>
                    <td className="py-4 px-4">Fear of "Bait & Switch"</td>
                    <td className="py-4 px-4">Preferred for budgeting</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4 font-medium">Best Software</td>
                    <td className="py-4 px-4"><Link to="/signup" className="underline text-black">Try QuoteTier</Link></td>
                    <td className="py-4 px-4"><Link to="/signup" className="underline text-black">Try QuoteTier</Link></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* When to use which */}
        <section className="py-16 md:py-24">
          <div className="container max-w-4xl mx-auto px-4">
            <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-8 text-center">Which One Should You Send?</h2>
            
            <div className="space-y-12">
              <div>
                <h3 className="font-instrument text-2xl font-bold mb-4 flex items-center text-blue-600">
                  <span className="bg-blue-100 text-blue-600 rounded-lg px-3 py-1 text-sm mr-3">Scenario A</span>
                  When to use an Estimate
                </h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  Use an estimate when you <strong>cannot guarantee the final price</strong> because you don't know exactly how much labor or materials will be needed.
                </p>
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <p className="font-bold text-sm mb-2 text-gray-900">EXAMPLE:</p>
                  <p className="italic text-gray-600">"I'm opening up a wall to fix a leak. I estimate it will cost $500 to patch the drywall, but if I find mold behind the insulation, the price will increase significantly."</p>
                </div>
              </div>

              <div>
                <h3 className="font-instrument text-2xl font-bold mb-4 flex items-center text-green-600">
                  <span className="bg-green-100 text-green-600 rounded-lg px-3 py-1 text-sm mr-3">Scenario B</span>
                  When to use a Quote
                </h3>
                <p className="font-jetbrains text-gray-700 leading-relaxed mb-4">
                  Use a quote when you have done this job 100 times and know <strong>exactly</strong> what it costs. Clients prefer quotes because it removes their financial risk.
                </p>
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <p className="font-bold text-sm mb-2 text-gray-900">EXAMPLE:</p>
                  <p className="italic text-gray-600">"I will install a new 50-gallon water heater. The price is $1,200, which includes the unit, labor, disposal of the old unit, and all tax. No hidden fees."</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* QuoteTier Solution */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 to-black text-white">
          <div className="container max-w-5xl mx-auto px-4 text-center">
            <h2 className="font-instrument text-4xl md:text-5xl font-bold mb-6">
              Create Both in QuoteTier
            </h2>
            <p className="font-jetbrains text-xl text-gray-200 mb-10 max-w-2xl mx-auto">
              Whether you need to send a flexible estimate or a fixed-price quote, QuoteTier makes it look professional, tracks when they open it, and lets them sign online.
            </p>
            
            <div className="grid gap-6 md:grid-cols-3 text-left mb-12">
               <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm border border-white/10">
                 <h3 className="font-bold text-lg mb-2">1. Choose Type</h3>
                 <p className="text-sm text-gray-300">Toggle between "Quote" and "Estimate" mode with one click. The legal disclaimer adjusts automatically.</p>
               </div>
               <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm border border-white/10">
                 <h3 className="font-bold text-lg mb-2">2. Add Options</h3>
                 <p className="text-sm text-gray-300">Add Good/Better/Best packages to up-sell clients regardless of which format you use.</p>
               </div>
               <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm border border-white/10">
                 <h3 className="font-bold text-lg mb-2">3. Get Signed</h3>
                 <p className="text-sm text-gray-300">Turn your estimate into a contract instantly with legally binding e-signatures.</p>
               </div>
            </div>

            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'guide-final-cta', action: 'signup' })}>
              <Button className="px-10 py-5 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-100 rounded-lg shadow-lg">
                Start Creating Free Quotes & Estimates
              </Button>
            </Link>
            <p className="mt-6 text-sm text-gray-400">No credit card required. Free forever.</p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default QuoteVsEstimateGuide;
